import React, { useRef, useState } from "react";

/**
 * Contains an image properly based on both width and height.
 *
 * We have a wide variety of image proportions: tall and skinny,
 * short and wide, and getting a nice layout when we don't know the
 * image ahead of time is virtually impossible with CSS only.
 *
 * Since it's not possible with pure CSS to properly contain an image
 * BOTH by width and height, this component does some calculations
 * and ensures that the image is properly contained, without being
 * distorted.
 *
 * maxHeight is required because CSS "height: 100%" does not do what you
 * expect it to do, and therefore we need to set some reasonable limits
 * to the maximum height allowed.
 */
export const ImageContainer: React.FC<{
  src: string;
  alt: string;
  maxHeight: number;
  maxWidth?: number;
  className?: string;
}> = ({ src, alt, maxWidth, maxHeight, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [dimensions, setDimensions] = useState<{
    width: number | string;
    height: number | string;
  }>({ width: 0, height: 0 });
  const auto = "auto";

  // calculate height / width once loaded
  const calculateImageSize = () => {
    let width: string;
    let height: string;

    const realWidth = imageRef.current?.naturalWidth || 0;
    const realHeight = imageRef.current?.naturalHeight || 0;

    if (!src) {
      width = maxWidth ? `${maxWidth}px` : auto;
      height = maxHeight ? `${maxHeight}px` : auto;
      setDimensions({ width, height });
      return;
    }

    if (!realHeight || !realHeight) {
      return;
    }

    let containBy = "height";
    const proportions = realWidth / realHeight;
    let testHeight: number;
    let testWidth: number;
    if (maxWidth) {
      testWidth = maxWidth;
      testHeight = Math.ceil(testWidth / proportions);
    } else {
      testHeight = maxHeight;
      testWidth = Math.ceil(maxHeight * proportions);
    }

    const containerWidth = containerRef.current?.offsetWidth || 10000;

    if (testHeight > maxHeight) {
      containBy = "width";
    }

    // if BOTH conditions are true, bias towards containing by width
    if (testWidth >= containerWidth || (maxWidth && testWidth >= maxWidth)) {
      containBy = "width";
    }

    if ("width" === containBy) {
      width = maxWidth ? `${maxWidth}px` : auto;
      height = auto;
    } else {
      height = `${maxHeight}px`;
      width = auto;
    }

    setDimensions({ width, height });
  };

  const { width, height } = dimensions;
  const classes = ["image-container", className].filter(c => !!c).join(" ");

  return (
    <div
      ref={containerRef}
      className={classes}
      style={{ height: `${maxHeight}px` }}
    >
      <img
        onLoad={calculateImageSize}
        ref={imageRef}
        src={src}
        alt={alt}
        style={{ width, height, maxWidth: "100%", maxHeight: "100%" }}
      />
    </div>
  );
};
